import { Button, Checkbox } from "@heroui/react";
import { PayOrderDialog } from "./pay-order-dialog";
import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  useAcceptAddressChange,
  type AddressChangeProposal,
  type ShipmentAddressCandidate,
} from "@/api/use-shipment-change-request";
const promiseTime = (
  value: string,
  precision: "DATE" | "DATETIME",
  timeZone: string
) =>
  precision === "DATE"
    ? new Date(value).toLocaleDateString(undefined, { timeZone: "UTC" })
    : new Date(value).toLocaleString(undefined, { timeZone });
const addressText = (value: ShipmentAddressCandidate | null) =>
  value
    ? [
        value.line1,
        value.line2,
        value.city,
        value.province,
        value.postalCode,
        value.country,
        value.plusCode,
        value.location &&
        Number.isFinite(value.location.lat) &&
        Number.isFinite(value.location.lng)
          ? `(${value.location.lat}, ${value.location.lng})`
          : null,
      ]
        .filter(Boolean)
        .join(", ")
    : "—";

export function AddressChangeProposalDecision({
  shipmentId,
  orderId,
  requestId,
  proposal,
}: {
  shipmentId: string;
  orderId: string;
  requestId: string;
  proposal: AddressChangeProposal;
}) {
  const { t } = useTranslation("app");
  const accept = useAcceptAddressChange();
  const [payOpen, setPayOpen] = useState(false);
  const [acceptedFingerprint, setAcceptedFingerprint] = useState<string | null>(
    null
  );
  const pending = proposal.state === "WAITING_CUSTOMER";
  const pickupQuote = proposal.offer?.pickupPackageQuote;
  const pickupMoney = (cents: number) =>
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);
  const money =
    proposal.commercialDeltaCents == null
      ? null
      : new Intl.NumberFormat("en-CA", {
          style: "currency",
          currency: "CAD",
        }).format(Math.abs(proposal.commercialDeltaCents) / 100);
  const expired =
    proposal.offer &&
    new Date(proposal.offer.expiresAt).getTime() <= Date.now();
  return (
    <div className="mt-2 space-y-2 rounded border border-default-200 p-3 text-sm">
      <p>
        {t(`shipmentChangeRequest.flow.states.${proposal.state}`, {
          defaultValue: proposal.state.split("_").join(" "),
        })}
      </p>
      {proposal.packageCandidate && (
        <section className="space-y-2 rounded border p-3">
          <h3 className="font-semibold">
            {t("shipmentChangeRequest.flow.proposedPackages")}
          </h3>
          <p>{t("shipmentChangeRequest.flow.packageListReplaces")}</p>
          {proposal.packageCandidate.map((piece) => (
            <dl
              key={piece.id}
              className="grid gap-1 border-t py-2 sm:grid-cols-2"
            >
              {(
                [
                  "id",
                  "index",
                  "kind",
                  "packageTypeId",
                  "weightGrams",
                  "lengthCm",
                  "widthCm",
                  "heightCm",
                  "weightOriginal",
                  "weightOriginalUnit",
                  "lengthOriginal",
                  "widthOriginal",
                  "heightOriginal",
                  "dimensionOriginalUnit",
                  "description",
                  "declaredValueCents",
                  "isFragile",
                  "requiresCooling",
                ] as const
              ).map((key) => (
                <div key={key}>
                  <dt className="font-medium">
                    {t(`shipmentHistory.fields.${key}`)}
                  </dt>
                  <dd>
                    {piece[key] == null
                      ? t("shipmentChangeRequest.flow.packageNotSpecified")
                      : typeof piece[key] === "boolean"
                        ? piece[key]
                          ? t("shipmentHistory.yes")
                          : t("shipmentHistory.no")
                        : String(piece[key])}
                  </dd>
                </div>
              ))}
            </dl>
          ))}
        </section>
      )}
      {proposal.changes.map((side) => (
        <div key={side.addressType}>
          <strong>
            {t(
              side.addressType === "FROM"
                ? "shipmentChangeRequest.flow.sender"
                : "shipmentChangeRequest.flow.recipient"
            )}
          </strong>
          <p>
            {t("shipmentChangeRequest.flow.before")}: {addressText(side.before)}
          </p>
          <p>
            {t("shipmentChangeRequest.flow.after")}: {addressText(side.after)}
          </p>
        </div>
      ))}
      {proposal.parties?.map((party) => (
        <div key={party.type}>
          <strong>
            {t(
              party.type === "FROM"
                ? "shipmentChangeRequest.flow.sender"
                : "shipmentChangeRequest.flow.recipient"
            )}
          </strong>
          <p>
            {party.name} {party.company} · {party.phone} · {party.email}
          </p>
          <p>{party.instructions}</p>
        </div>
      ))}
      {proposal.offer && (
        <div>
          <p>
            {t("shipmentChangeRequest.flow.offerCurrency")}:{" "}
            {proposal.offer.currency}
          </p>
          <strong>
            {proposal.offer.service.name} ({proposal.offer.service.code})
          </strong>
          <p>
            {t("shipmentChangeRequest.flow.delivery")}:{" "}
            {promiseTime(
              proposal.offer.deliveredFastest,
              proposal.offer.deliveredPrecision,
              proposal.offer.timeZone
            )}{" "}
            –{" "}
            {promiseTime(
              proposal.offer.deliveredSlowest,
              proposal.offer.deliveredPrecision,
              proposal.offer.timeZone
            )}
          </p>
          {proposal.offer.readyAt && (
            <p>
              {t("shipmentChangeRequest.flow.readyAt")}:{" "}
              {promiseTime(
                proposal.offer.readyAt,
                "DATETIME",
                proposal.offer.timeZone
              )}{" "}
              · {proposal.offer.timeZone}
            </p>
          )}
          {proposal.offer.pickupDate && (
            <p>
              {t("shipmentChangeRequest.flow.pickupDate")}:{" "}
              {promiseTime(
                proposal.offer.pickupDate,
                "DATE",
                proposal.offer.timeZone
              )}
            </p>
          )}
          {proposal.offer.handoffBy && (
            <p>
              {t("shipmentChangeRequest.flow.handoffBy")}:{" "}
              {promiseTime(
                proposal.offer.handoffBy,
                "DATETIME",
                proposal.offer.timeZone
              )}{" "}
              · {proposal.offer.timeZone}
            </p>
          )}
          {proposal.offer.dispatch && (
            <p>
              {t("shipmentChangeRequest.flow.dispatch")}:{" "}
              {addressText(proposal.offer.dispatch)}
            </p>
          )}
          {proposal.offer.dropoff && (
            <p>
              {t("shipmentChangeRequest.flow.dropoff")}:{" "}
              {addressText(proposal.offer.dropoff)}
            </p>
          )}
        </div>
      )}
      {proposal.offer?.pickupVisit && (
        <div className="rounded border p-3">
          <strong>{t("shipmentChangeRequest.flow.actualPickup")}</strong>
          <p>
            {t("shipmentChangeRequest.flow.before")}:{" "}
            {addressText(proposal.offer.pickupVisit.before)}
          </p>
          <p>
            {t("shipmentChangeRequest.flow.after")}:{" "}
            {addressText(proposal.offer.pickupVisit.after)}
          </p>
          <p>
            {proposal.offer.pickupVisit.service.name} —{" "}
            {proposal.offer.pickupVisit.scheduledDate.slice(0, 10)} —{" "}
            {proposal.offer.pickupVisit.slot.name}{" "}
            {proposal.offer.pickupVisit.slot.startTime}–
            {proposal.offer.pickupVisit.slot.endTime}
          </p>
          <p>
            {proposal.offer.pickupVisit.contactName} ·{" "}
            {proposal.offer.pickupVisit.contactPhone} ·{" "}
            {proposal.offer.pickupVisit.contactEmail}
          </p>
          <p>{proposal.offer.pickupVisit.instructions}</p>
          <p>
            {t("shipmentChangeRequest.flow.pickupPrice", {
              before: (proposal.offer.pickupVisit.oldGrossCents / 100).toFixed(
                2
              ),
              after: (proposal.offer.pickupVisit.newGrossCents / 100).toFixed(
                2
              ),
            })}
          </p>
        </div>
      )}
      {pickupQuote && (
        <div className="rounded border p-3 space-y-1">
          <strong>
            {t("shipmentChangeRequest.flow.reviewedPickup")} (
            {pickupQuote.currency})
          </strong>
          <p>
            {t("shipmentChangeRequest.flow.previousPickup")}:{" "}
            {t("shipmentChangeRequest.flow.pickupNet")}{" "}
            {pickupMoney(pickupQuote.oldNetCents)} +{" "}
            {t("shipmentChangeRequest.flow.pickupTax")}{" "}
            {pickupMoney(pickupQuote.oldTaxCents)}
          </p>
          <p>
            {t("shipmentChangeRequest.flow.proposedPickup")}:{" "}
            {t("shipmentChangeRequest.flow.pickupNet")}{" "}
            {pickupMoney(pickupQuote.newNetCents)} +{" "}
            {t("shipmentChangeRequest.flow.pickupTax")}{" "}
            {pickupMoney(pickupQuote.newTaxCents)}
          </p>
          <p>
            {t("shipmentChangeRequest.flow.pickupDifference")}:{" "}
            {pickupMoney(pickupQuote.deltaCents)}
          </p>
          <p>{t("shipmentChangeRequest.flow.pickupIncluded")}</p>
        </div>
      )}
      {money && (
        <p>
          {t(
            (proposal.commercialDeltaCents ?? 0) < 0
              ? "shipmentChangeRequest.flow.refund"
              : (proposal.commercialDeltaCents ?? 0) > 0
                ? "shipmentChangeRequest.flow.charge"
                : "shipmentChangeRequest.flow.noCharge",
            { amount: money }
          )}
        </p>
      )}
      {proposal.commercialTaxCents != null && (
        <p>
          {t("shipmentChangeRequest.flow.taxIncluded", {
            amount: new Intl.NumberFormat("en-CA", {
              style: "currency",
              currency: "CAD",
            }).format(proposal.commercialTaxCents / 100),
          })}
        </p>
      )}
      {proposal.commercialReason && <p>{proposal.commercialReason}</p>}
      {pending && (
        <>
          <Checkbox
            isSelected={acceptedFingerprint === proposal.fingerprint}
            onValueChange={(checked) =>
              setAcceptedFingerprint(checked ? proposal.fingerprint : null)
            }
          >
            {t("shipmentChangeRequest.flow.consent")}
          </Checkbox>
          <Button
            type="button"
            className="rounded bg-primary px-3 py-2 text-primary-foreground disabled:opacity-40"
            isDisabled={
              accept.isPending ||
              acceptedFingerprint !== proposal.fingerprint ||
              Boolean(expired) ||
              money == null
            }
            onPress={() =>
              accept.mutate({
                shipmentId,
                requestId,
                fingerprint: proposal.fingerprint,
              })
            }
          >
            {t("shipmentChangeRequest.flow.accept")}
          </Button>
          {expired && (
            <p role="alert">{t("shipmentChangeRequest.flow.expired")}</p>
          )}
        </>
      )}
      {proposal.state === "WAITING_FUNDING" && (
        <p>
          {t(
            (proposal.commercialDeltaCents ?? 0) > 0
              ? "shipmentChangeRequest.flow.payOrder"
              : "shipmentChangeRequest.flow.waitRefund"
          )}
        </p>
      )}
      {proposal.state === "WAITING_FUNDING" &&
        typeof proposal.commercialDeltaCents === "number" &&
        proposal.commercialDeltaCents > 0 && (
          <>
            <Button
              type="button"
              className="rounded border border-primary px-3 py-2"
              onPress={() => setPayOpen(true)}
            >
              {t("shipmentChangeRequest.flow.payAdjustment")}
            </Button>
            <PayOrderDialog
              open={payOpen}
              onOpenChange={setPayOpen}
              orderId={orderId}
              totalCents={proposal.commercialDeltaCents}
              purpose="ADDRESS_CHANGE"
              addressChangeShipmentId={shipmentId}
              addressChangeRequestId={requestId}
              allowInvoice={false}
            />
          </>
        )}
      {accept.error && (
        <p role="alert" className="text-danger">
          {axios.isAxiosError<{ message?: string }>(accept.error)
            ? (accept.error.response?.data.message ??
              t("shipmentChangeRequest.flow.failed"))
            : t("shipmentChangeRequest.flow.failed")}
        </p>
      )}
    </div>
  );
}
